'use client';

import React, { useState } from 'react';
import { toastr } from 'react-redux-toastr';

import { Box } from '@nerdwallet/currency/components/Box';
import { CheckboxOption } from '@nerdwallet/currency/components/CheckboxOption';
import { Input } from '@nerdwallet/currency/components/Input';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';
import { Type } from '@nerdwallet/currency/components/Type';

import { gql, useMutation, useLazyQuery } from '@apollo/client';
import FriendlyTitle from '@components/transunion-flow/steps/components/friendly-title';
import ContinueButton from '@components/transunion-flow/steps/components/continue-button';
import {
  INITIALIZE_CREDIT_PROFILE_MUTATION,
  TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
} from '@components/transunion-flow/steps/queries';
import {
  CURRENT_TU_TERMS_OF_SERVICE,
  CURRENT_TU_TERMS_OF_SERVICE_VERSION,
  TU_TERMS_OF_SERVICE_SOURCE,
  TU_TERMS_OF_SERVICE_TYPE,
} from '@components/transunion-flow/steps/constants';

import { SegmentPageView } from '@lib/segment';
import { getLogger } from '@lib/logging';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import {
  CreditProfileAuthCustomerInput,
  CreditProfileAuthStatusProfile,
} from '@generated-gql/generated-types';
import styles from './short-ssn.module.scss';
import {
  generateCreateTUCustomerErrorMessage,
  getDuplicateEmailFromError,
  isDeactivated,
  isPending,
  isTUBlocked,
} from '@components/transunion-flow/utils';

const logger = getLogger('Short SSN Step');

const isFourDigits = (text: string): boolean => {
  return /^\d{4}$/i.test(text);
};

const ACKNOWLEDGE_TERMS_OF_SERVICE_MUTATION = gql`
  mutation AcknowledgeTuTermsOfService(
    $input: AcknowledgeTermsOfServiceInput!
  ) {
    acknowledgeTermsOfService(input: $input) {
      success
    }
  }
`;

interface Props {
  onInitializeSuccess: (profile: CreditProfileAuthStatusProfile) => void;
}

const ShortSSNStep = ({ onInitializeSuccess }: Props) => {
  const { setTuVisibility } = useTuVisibilityContext();
  const {
    tosAcknowledged,
    setTosAcknowledged,
    tuCustomerInfo,
    setTuCustomerInfo,
    setAuthStatusProfile,
    setTuErrors,
  } = useTuStepper();

  const [partialSSN, setPartialSSN] = useState<string>('');
  const [confirmPartialSSN, setConfirmPartialSSN] = useState<string>('');
  const [agree, setAgree] = useState<boolean>(false);
  const [validation, setValidation] = useState({
    partialSSN: { isValid: null, feedback: null },
    confirmPartialSSN: { isValid: null, feedback: null },
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [getAuthenticationStatusProfile] = useLazyQuery(
    TU_AUTHENTICATION_STATUS_PROFILE_QUERY,
  );

  const handleCreateTuCustomerSuccess = (
    data: CreditProfileAuthStatusProfile,
  ) => {
    onInitializeSuccess(data);
    setIsLoading(false);
  };

  const handleCreateTuCustomerFail = async (createError) => {
    // Retrieve authentication status profile
    const { data, error } = await getAuthenticationStatusProfile({
      fetchPolicy: 'network-only',
    });
    setIsLoading(false);
    if (data && !error) {
      const tasp = data.tuAuthenticationStatusProfile;
      setAuthStatusProfile(tasp);

      const errorMsg = generateCreateTUCustomerErrorMessage(createError);
      if (errorMsg) {
        toastr.error('', errorMsg);
      }

      const duplicateEmail = getDuplicateEmailFromError(createError);
      if (isTUBlocked(tasp) || duplicateEmail) {
        if (duplicateEmail) {
          // if the user is detected to have an active TU profile already with another email
          setTuErrors({
            duplicateEmail,
          });
        }
        setTuVisibility({
          show: true,
          tuStep: TuStep.TU_BLOCKED,
        });
        return;
      }

      // If the initialize call fails to find the user (but sets their TASP status to PENDING or DEACTIVATED),
      // prompt for their full SSN.
      // (DEACTIVATED is for users who deactivated but are going through the auth flow again)
      if (isPending(tasp) || isDeactivated(tasp)) {
        setTuVisibility({
          show: true,
          tuStep: TuStep.TU_FULL_SSN,
        });
        return;
      }
    }
  };

  const [createTUCustomer] = useMutation(INITIALIZE_CREDIT_PROFILE_MUTATION, {
    onCompleted: (data) => {
      handleCreateTuCustomerSuccess(data.initializeCreditProfile);
    },
    onError: handleCreateTuCustomerFail,
  });
  const [acknowledgeTOS] = useMutation(ACKNOWLEDGE_TERMS_OF_SERVICE_MUTATION, {
    variables: {
      input: {
        type: TU_TERMS_OF_SERVICE_TYPE,
        source: TU_TERMS_OF_SERVICE_SOURCE,
        version: CURRENT_TU_TERMS_OF_SERVICE_VERSION,
      },
    },
    onError: (err) => {
      setIsLoading(false);
      logger.warn('Error creating TU customer with TU acknowledge:', err);
    },
  });

  const onSubmit = async (event) => {
    event.preventDefault();

    const ssnData: CreditProfileAuthCustomerInput = {
      ...tuCustomerInfo,
      partialSsn: partialSSN,
      fullSsn: undefined,
    };

    // locally store data in the case createTUCustomer needs to be executed again
    setTuCustomerInfo(ssnData);

    setIsLoading(true);
    if (tosAcknowledged) {
      await createTUCustomer({
        variables: {
          customer: ssnData,
        },
      });
    } else {
      await acknowledgeTOS().then(async () => {
        setTosAcknowledged(true);
        await createTUCustomer({
          variables: {
            customer: ssnData,
          },
        });
      });
    }
  };

  const onChangeSSN = (e, field) => {
    const finalValue = e.target.value.replace(/\D/g, '');

    const [otherField, otherFieldValue] =
      field === 'partialSSN'
        ? ['confirmPartialSSN', confirmPartialSSN]
        : ['partialSSN', partialSSN];

    const isValidFormat = isFourDigits(finalValue);
    const isConfirmed = isFourDigits(otherFieldValue)
      ? finalValue === otherFieldValue
      : true;

    const isValid = isValidFormat && isConfirmed;

    let feedback;
    if (!isValidFormat) {
      feedback = 'Please enter 4 numbers';
    }
    if (!isConfirmed) {
      feedback = 'SSN numbers must match';
    }

    if (otherField === 'partialSSN') {
      setConfirmPartialSSN(finalValue);
    } else {
      setPartialSSN(finalValue);
    }
    setValidation({
      ...validation,
      [field]: {
        isValid,
        feedback: isValid ? undefined : feedback,
      },
    });
  };

  const handleCheckboxChange = (e) => {
    setAgree(e.target.checked);
  };

  const isDisabled =
    !agree ||
    !validation.confirmPartialSSN.isValid ||
    !validation.partialSSN.isValid;

  return (
    <SegmentPageView name="tu_short_ssn">
      <form onSubmit={onSubmit}>
        <FriendlyTitle
          title={'What are the last 4 digits of your SSN?'}
          subtext={
            'Rest easy knowing this is only used to verify your identity.'
          }
        />

        <Box display="flex" marginTop={2}>
          <Input
            error={validation.partialSSN.isValid === false}
            errorMessage={validation.partialSSN.feedback}
            fullWidth
            label={'Last four digits of your SSN'}
            onChange={(e) => onChangeSSN(e, 'partialSSN')}
            onBlur={(e) => onChangeSSN(e, 'partialSSN')}
            value={partialSSN}
            type="tel"
            maxLength={4}
            name={'partialSSN'}
            placeholder="XXXX"
          />
        </Box>

        <Box display="flex" marginTop={2} marginBottom={4}>
          <Input
            error={validation.confirmPartialSSN.isValid === false}
            errorMessage={validation.confirmPartialSSN.feedback}
            fullWidth
            label={'Confirm the last four digits of your SSN'}
            onChange={(e) => onChangeSSN(e, 'confirmPartialSSN')}
            onBlur={(e) => onChangeSSN(e, 'confirmPartialSSN')}
            value={confirmPartialSSN}
            type="tel"
            maxLength={4}
            name={'confirmPartialSSN'}
            placeholder="XXXX"
          />
        </Box>

        <CheckboxOption
          checked={agree}
          inline
          label="I agree to the terms below"
          onChange={handleCheckboxChange}
        />
        <Type size={1} color="neutral-light" className={styles.termsOfService}>
          {CURRENT_TU_TERMS_OF_SERVICE}
        </Type>

        <div className={styles.buttonContainer}>
          <ContinueButton
            isDisabled={isDisabled || isLoading}
            isLoading={isLoading}
          />
        </div>
        <Box marginTop={4}>
          <SecureEncryptionMessage />
        </Box>
      </form>
    </SegmentPageView>
  );
};

export default ShortSSNStep;
